import { useEffect } from "react";
import { Navigate, Outlet, useNavigate, useOutletContext } from "react-router";

import { PageHeading, Spinner } from "@prequel/shared-components";

import type { ProductResource } from "@/api/import/products";
import { useProduct } from "@/api/import/products";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type ProductContext = {
  product: ProductResource;
};

export function useProductContext(): ProductContext {
  return useOutletContext<ProductContext>();
}

const ProductPage = () => {
  const productID = useRequiredParam("productID");
  const { addToast } = useToast();
  const { data: product, error, isLoading } = useProduct(productID);

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load product." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!product) {
    return <Navigate to="/import/destinations/products" replace />;
  }

  return <Outlet context={{ product }} />;
};

export const ProductDetailsPage = () => {
  const navigate = useNavigate();
  const { product } = useProductContext();

  return (
    <div className="max-w-4xl px-7 py-4">
      <PageHeading
        title={product.name}
        onGoBack={() => navigate("/import/destinations/products")}
        breadcrumbs={<Breadcrumbs dynamicLabel={product.name} />}
      />
      <div className="flex-1">
        <div className="relative">
          <div className="pt-0 pb-16">
            <Outlet context={{ product }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
